<template>
  <template v-if="listView">
    <EventsListCard :event="event" />
  </template>
  <template v-else>
    <div
      class="group relative bg-white dark:bg-gray-800/50 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
      :class="[eventStatusClass]"
    >
      <!-- Event Cover Image -->
      <div class="relative h-48 rounded-t-xl overflow-hidden bg-black">
        <nuxt-link :to="'/events/' + event.attributes.slug">
          <img
            :src="
              event.attributes.cover?.data?.attributes?.url ||
              '/images/placeholder.jpg'
            "
            :alt="event.attributes.title"
            class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div
            class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"
          ></div>
        </nuxt-link>
      </div>

      <div class="p-6">
        <!-- Event Type and Status -->
        <div class="flex items-center justify-between mb-4">
          <span
            v-if="event.attributes.aa_events_type?.data"
            class="px-3 py-1 text-xs font-medium bg-primary text-white rounded-full flex items-center gap-1"
          >
            <Icon :name="getEventTypeIcon" class="w-3 h-3" />
            {{ event.attributes.aa_events_type.data.attributes.title }}
          </span>

          <div
            v-if="eventStatus"
            class="text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1"
            :class="eventStatusColorClass"
          >
            <Icon :name="eventStatusIcon" class="w-3 h-3" />
            {{ eventStatus }}
          </div>
        </div>

        <!-- Title and Verification -->
        <div class="mb-4">
          <h3
            class="text-xl font-bold text-primary dark:text-white group-hover:text-primary/80 transition-colors"
          >
            <span class="flex items-center gap-1.5">
              <!-- Adjusted gap -->
              <!-- Use the computed property for the title -->
              <span class="line-clamp-1 flex items-center"
                >{{ truncatedTitle }}
                <client-only>
                  <Vue3Lottie
                    v-if="event.attributes.isPublished"
                    class="w-5 h-5 flex-shrink-0"
                    animationLink="/animation/10.json"
                    :height="25"
                    :width="25"
                  /> </client-only
              ></span>

              <!-- Verification Badge -->
            </span>
          </h3>
        </div>

        <!-- Date Range -->
        <div
          class="flex items-center text-primary/70 dark:text-gray-300 mb-4 text-sm"
        >
          <Icon name="heroicons:calendar" class="w-5 h-5 mr-2" />
          <span>
            <NuxtTime
              :datetime="event.attributes.starts"
              format="DD MMM YYYY"
            />
            <template v-if="event.attributes.starts !== event.attributes.ends">
              <span class="mx-2">-</span>
              <NuxtTime
                :datetime="event.attributes.ends"
                format="DD MMM YYYY"
              />
            </template>
          </span>
        </div>

        <!-- Description -->
        <MDC
          class="text-primary/70 dark:text-gray-300 line-clamp-2 mb-4 text-sm"
          :value="event.attributes.description"
        />

        <!-- Categories and Tags -->
        <div class="flex flex-wrap gap-2 mb-6">
          <span
            class="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full dark:bg-primary/20 dark:text-primary-light"
          >
            {{ event.attributes.aa_category.data?.attributes.title }}
          </span>
          <span
            v-for="tag in event.attributes.aa_tags.data"
            :key="tag.id"
            class="px-3 py-1 text-xs font-medium bg-primary/5 text-primary/80 rounded-full hover:bg-primary/10 transition-colors cursor-pointer"
          >
            {{ tag.attributes.title }}
          </span>
        </div>

        <!-- Action Button -->
        <nuxt-link
          :to="'/events/' + event.attributes.slug"
          class="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 focus:ring-4 focus:ring-primary/30 transition-all group"
        >
          Learn More
          <Icon
            name="heroicons:arrow-right"
            class="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"
          />
        </nuxt-link>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
// In the props interface, add aa_events_type
const props = defineProps<{
  event: {
    id: number;
    attributes: {
      title: string;
      description: string;
      slug: string;
      starts: string;
      ends: string;
      isPublished: boolean;
      cover?: {
        data?: {
          attributes?: {
            url: string;
          };
        };
      };
      aa_category: {
        data: {
          attributes: {
            title: string;
          };
        };
      };
      aa_events_type: {
        data: {
          attributes: {
            title: string;
            icon?: string;
          };
        };
      };
      aa_tags: {
        data: Array<{
          id: number;
          attributes: {
            title: string;
          };
        }>;
      };
    };
  };
  listView?: boolean;
}>();

// Fix date comparisons by using UTC timestamps
const isHappening = computed(() => {
  const start = new Date(props.event.attributes.starts);
  const end = new Date(props.event.attributes.ends);
  const now = new Date();

  // Reset time parts to compare just the dates
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  now.setHours(0, 0, 0, 0);

  return now >= start && now <= end;
});

const isPast = computed(() => {
  const end = new Date(props.event.attributes.ends);
  const now = new Date();

  return now > end;
});

const isScheduled = computed(() => {
  const start = new Date(props.event.attributes.starts);
  const now = new Date();

  start.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);

  const diffTime = start.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return !isPast.value && !isHappening.value && diffDays > 30;
});

const isUpcoming = computed(() => {
  const start = new Date(props.event.attributes.starts);
  const now = new Date();

  start.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);

  const diffTime = start.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return !isPast.value && !isHappening.value && diffDays <= 30;
});

// Add responsive helper
const isMobile = computed(() => {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 640;
});

// Add window resize listener
onMounted(() => {
  window.addEventListener("resize", () => {
    isMobile.value = window.innerWidth < 640;
  });
});

onUnmounted(() => {
  window.removeEventListener("resize", () => {
    isMobile.value = window.innerWidth < 640;
  });
});

// Event status computations
const eventStatus = computed(() => {
  if (isPast.value) return "Event Ended";
  if (isHappening.value) return "Happening Now";
  if (isUpcoming.value) return "Upcoming";
  if (isScheduled.value) return "Scheduled";
  return "";
});

const eventStatusColorClass = computed(() => {
  if (isPast.value)
    return "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300";
  if (isHappening.value)
    return "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300";
  if (isUpcoming.value)
    return "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300";
  return "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300";
});

const eventStatusIcon = computed(() => {
  if (isPast.value) return "heroicons:clock";
  if (isHappening.value) return "heroicons:bolt";
  if (isUpcoming.value) return "heroicons:fire";
  return "heroicons:calendar";
});

const eventStatusClass = computed(() => {
  if (isPast.value) return "border border-red-500/20";
  if (isHappening.value) return "border border-yellow-500/20";
  if (isUpcoming.value) return "border border-green-500/20";
  if (isScheduled.value) return "border border-blue-500/20";
  return "border border-gray-100 dark:border-gray-700/50";
});

const getEventTypeIcon = computed(
  () =>
    props.event.attributes.aa_events_type?.data?.attributes?.icon ||
    "heroicons:academic-cap"
);

const characterLimit = 45; // Define the limit

const truncatedTitle = computed(() => {
  const title = props.event.attributes.title;
  if (title.length > characterLimit) {
    return title.slice(0, characterLimit) + "...";
  }
  return title;
});
</script>
